import React, { ReactElement, useCallback, useMemo } from 'react';
import {
  BarDataType as FooterBarDataType,
  ContactDataType as FooterContactDataType,
  Footer as FooterComponent,
  SectionType as FooterSectionType,
  SocialDataType as FooterSocialDataType,
} from 'b2b-components/theme/components/Footer';
import { LogoSliderItem } from 'b2b-apps/shop/app/components/App/Footer/Footer';
import { NewsletterForm } from 'b2b-apps/shop/app/components/App/Footer/NewsletterForm';
import { createKey } from 'b2b-apps/shop/common/utils/key/key';
import { getAbsoluteUrl, isExternalUrl } from 'b2b-apps/shop/routes/utils/url';
import { ComponentType, Props, SectionType } from './';
import './Footer.scss';

export const Footer: ComponentType = ({
  data,
  hasNewsletterDisplayed = false,
  location,
  onLinkClick,
  text,
  logoSliderData,
}: Props): ReactElement => {
  const {
    contact,
    footer_bar: footerBar,
    sections,
  } = data;
  const barSocialData = useMemo(
    (): FooterSocialDataType => {
      if (!footerBar) {
        return {
          facebook: null,
          instagram: null,
          linkedin: null,
          twitter: null,
          youtube: null,
        };
      }

      return {
        facebook: footerBar.facebook ? footerBar.facebook.href || null : null,
        instagram: footerBar.instagram ? footerBar.instagram.href || null : null,
        linkedin: footerBar.linkedin ? footerBar.linkedin.href || null : null,
        twitter: footerBar.twitter ? footerBar.twitter.href || null : null,
        youtube: footerBar.youtube ? footerBar.youtube.href || null : null,
      };
    },
    [footerBar],
  );

  const barLinks = useMemo(
    (): ReactElement => (
      <ul className="cds-List cds-List--Unstyled cds-List--Inline">
        {footerBar?.link.map((link: LinkType, index: number) => (
          <li key={createKey(link.title, index)}>
            <a
              className="cds-Footer-Link"
              href={link.href}
              onClick={onLinkClick(link.href)}
            >
              {link.title}
            </a>
          </li>
        ))}
      </ul>
    ),
    [footerBar, onLinkClick],
  );

  const barData = useMemo(
    (): FooterBarDataType => ({
      social: barSocialData,
      links: barLinks,
    }),
    [barSocialData, barLinks],
  );

  const contactData = useMemo(
    (): FooterContactDataType | null => {
      if (!contact) {
        return null;
      }

      const { address, email, label, phone } = contact;

      return {
        address: address.split('\n').map((line: string) => <div key={line}>{line}</div>),
        email: (
          <a href={`mailto:${email}`} className="cds-Footer-Link">
            {text.email(email)}
          </a>
        ),
        label,
        phoneNumber: (
          <a href={`tel:${phone}`} className="cds-Footer-Link">
            {text.phone(phone)}
          </a>
        ),
      };
    },
    [contact, text],
  );

  const renderSectionLinks = useCallback(
    (links: LinkType[]): ReactElement => (
      <ul className="cds-FooterSection-List">
        {links.map(link => ({
          ...link,
          href: link.href.replace(' ', ''),
        })).map((link) => (
          <li className="cds-FooterSection-ListItem" key={link.title}>
            <a
              className="cds-Footer-Link"
              href={link.href}
              onClick={onLinkClick(link.href)}
            >
              {link.title}
            </a>
          </li>
        ))}
      </ul>
    ),
    [onLinkClick],
  );

  const sectionsData = useMemo(
    (): FooterSectionType[] => {
      if (!sections) {
        return [];
      }

      return sections.map((section: SectionType) => ({
        label: section.label,
        content: renderSectionLinks(section.link),
      }));
    },
    [renderSectionLinks, sections],
  );

  const logos = useMemo(
    () => ({
      title: logoSliderData.title,
      items: logoSliderData.items.reduce(
        (
          result: ReactElement[],
          logo: LogoSliderItem,
        ): ReactElement[] => {
          result.push(
            // eslint-disable-next-line react/jsx-no-target-blank
            <a
              href={isExternalUrl(logo.url) ? logo.url : getAbsoluteUrl(logo.url)}
              onClick={() => onLinkClick(logo.url)}
              rel={isExternalUrl(logo.url) ? 'noopener noreferrer' : undefined}
              target={isExternalUrl(logo.url) ? '_blank' : undefined}
            >
              <img
                alt={logo.brandName}
                src={logo.src}
              />
            </a>,
          );

          return result;
        },
        [],
      ),
    }),
    [
      logoSliderData,
      onLinkClick,
    ],
  );

  const renderNewsletterForm = useCallback(
    (): ReactElement => (
      <NewsletterForm key={`${location.pathname}${location.search}`} />
    ),
    [location.pathname, location.search],
  );

  return (
    <footer className="Footer">
      <FooterComponent
        bar={barData}
        contact={contactData}
        hasNewsletterDisplayed={hasNewsletterDisplayed}
        renderNewsletterForm={renderNewsletterForm}
        sections={sectionsData}
        text={text}
        logos={logos}
      />
    </footer>
  );
};
